import {
  createFrontendModule,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import searchPlugin from '@backstage/plugin-search/alpha';
import { searchApiRef } from '@backstage/plugin-search-react';
import {
  SearchFilterBlueprint,
  SearchFilterResultTypeBlueprint,
  SearchResultListItemBlueprint,
} from '@backstage/plugin-search-react/alpha';
import { ResilientSearchApi } from './ResilientSearchApi';

/*
 * The vendor `SearchClient` throws on a `MissingIndexError` — a collator
 * that has not yet indexed a single document (announcements with zero rows
 * in local dev is the standing example) — which surfaces to `SearchResult`
 * as a raw error panel instead of an empty result set.
 */
const searchApi = searchPlugin.getExtension('api:search').override({
  factory: originalFactory =>
    originalFactory({
      params: defineParams =>
        defineParams({
          api: searchApiRef,
          deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
          factory: ({ discoveryApi, fetchApi }) =>
            new ResilientSearchApi(discoveryApi, fetchApi),
        }),
    }),
});

/*
 * Replaces the results page rendered at `/search`.
 *
 * Two functional gaps in the vendor page: `SearchType.Accordion` fetches its
 * facet counts with `Promise.all`, so one collator without an index blanks every
 * count; and pagination is a Previous/Next pager with no page numbers. Both live
 * inside the page's loader, which is not an extension input, so the page itself
 * is the smallest thing that can be replaced.
 *
 * `override({ factory })` rather than `override({ params })` because the
 * replacement still needs the page's inputs — the result item components, extra
 * result types and filters that other plugins contribute. Overriding only the
 * loader keeps the stock path, title, icon and `rootRouteRef`.
 */
const searchPage = searchPlugin.getExtension('page:search').override({
  factory(originalFactory, { config, inputs }) {
    const items = inputs.items.map(item =>
      item.get(SearchResultListItemBlueprint.dataRefs.item),
    );
    const resultTypes = inputs.resultTypes.map(item =>
      item.get(SearchFilterResultTypeBlueprint.dataRefs.resultType),
    );
    const searchFilters = inputs.searchFilters.map(
      item => item.get(SearchFilterBlueprint.dataRefs.searchFilters).component,
    );

    return originalFactory({
      params: {
        loader: async () => {
          const { SearchResultsPage } = await import('./SearchResultsPage');
          return (
            <SearchResultsPage
              items={items}
              resultTypes={resultTypes}
              searchFilters={searchFilters}
              noTrack={config.noTrack}
            />
          );
        },
      },
    });
  },
});

export const searchModule = createFrontendModule({
  pluginId: 'search',
  extensions: [searchPage, searchApi],
});
